/**
 * FormulaErrorTooltip Component
 * Story 6.3: Integrate HyperFormula Engine
 * AC-6.3.6: Error display in cells with tooltip explanation
 *
 * Displays formula error codes with explanatory tooltips.
 */

import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { FormulaError } from '../../../services';

export interface FormulaErrorTooltipProps {
  error: FormulaError;
  className?: string;
}

/**
 * Human-readable explanations for formula error codes
 * Story 6.6: Added cross-sheet reference error explanations
 */
const errorExplanations: Record<string, string> = {
  '#REF!': 'The formula references a cell, column, or sheet that no longer exists or is invalid.',
  '#VALUE!': 'The formula uses an incorrect data type (e.g., text where a number is expected).',
  '#DIV/0!': 'The formula attempts to divide by zero or an empty cell.',
  '#NAME?': 'The formula contains an unrecognized function name or reference.',
  '#N/A': 'A value is not available or cannot be found (common with lookup functions).',
  '#NUM!': 'The formula produces an invalid numeric result (too large, too small, or invalid).',
  '#NULL!': 'The formula references an intersection of two ranges that do not intersect.',
  '#CYCLE!': 'The formula creates a circular reference across one or more sheets.',
  '#ERROR!': 'An unexpected error occurred while evaluating the formula.',
};

/**
 * Story 6.6: AC-6.6.4, AC-6.6.5 - Cross-sheet specific error patterns
 * Returns a more specific error message if the error is cross-sheet related
 */
export function getCrossSheetErrorMessage(error: FormulaError): string | null {
  const msg = error.message?.toLowerCase() || '';

  // Check for missing sheet errors
  if (error.code === '#REF!' && msg.includes('sheet')) {
    const sheetMatch = error.message?.match(/sheet[:\s]+['"]?([^'"]+)['"]?/i);
    if (sheetMatch) {
      return `Referenced sheet '${sheetMatch[1]}' not found. It may have been deleted or renamed.`;
    }
    return 'The referenced sheet no longer exists or is inaccessible.';
  }

  // Check for missing column in cross-sheet ref
  if (error.code === '#REF!' && msg.includes('column')) {
    return 'The referenced column does not exist in the target sheet.';
  }

  // Circular reference across sheets
  if (error.code === '#CYCLE!') {
    return 'Circular dependency detected: This formula depends on a cell that ultimately references back to this cell, possibly across multiple sheets.';
  }

  return null;
}

/**
 * FormulaErrorTooltip - Displays formula errors with hover explanations
 * Story 6.6: Enhanced with cross-sheet specific error messages
 */
export function FormulaErrorTooltip({ error, className = '' }: FormulaErrorTooltipProps): React.ReactElement {
  const [showTooltip, setShowTooltip] = useState(false);

  // Story 6.6: Try cross-sheet specific message first, fall back to general
  const crossSheetMsg = getCrossSheetErrorMessage(error);
  const explanation = crossSheetMsg ?? errorExplanations[error.code] ?? error.message;

  return (
    <div
      className={`relative inline-flex items-center gap-1 ${className}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <AlertTriangle className="h-3 w-3 text-red-600 dark:text-red-500 flex-shrink-0" />
      <span className="text-red-600 dark:text-red-500 font-mono text-sm font-semibold">{error.code}</span>

      {showTooltip && (
        <div
          className="absolute z-50 bottom-full left-0 mb-1 w-64 p-2
                     bg-gray-900 text-white text-xs rounded shadow-lg"
          role="tooltip"
        >
          <div className="font-semibold mb-1">{error.code}</div>
          <div className="text-gray-300">{explanation}</div>
          {error.message && error.message !== explanation && (
            <div className="mt-1 text-gray-400 text-[10px] italic">
              Details: {error.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Check if a value is a formula error code
 */
export function isFormulaErrorCode(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  return value.startsWith('#') && value.endsWith('!') || value === '#NAME?' || value === '#N/A';
}

/**
 * Get error styling classes for a cell with a formula error
 * Uses stronger colors for better readability in both light and dark modes
 */
export function getFormulaErrorCellStyles(): string {
  return 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-500';
}

export default FormulaErrorTooltip;
